/**
 * Selectors for the post-solve result-editing feature.
 *
 * All heavy artifacts (cost index, eval context, golden fidelity report) are
 * memoized on the store references that feed them, so they cost nothing until
 * the results panel is actually used and are rebuilt only when the underlying
 * tables change.
 */
import { createSelector } from '@reduxjs/toolkit';

import { evaluateAmendment } from 'store/functions/resultEditing/amendments';
import { buildEvalContext } from 'store/functions/resultEditing/evaluator';
import { goldenCheck } from 'store/functions/resultEditing/goldenCheck';
import { suggestRepairs } from 'store/functions/resultEditing/repairSuggestions';

const selectData = (state) => state.inputData.present.data;
const selectTimeUnit = (state) =>
  state.inputData.present.generalSettings?.time?.timeUnit || 'clock';

/** Evaluation context (lookups + capacity policy) for the current tables. */
export const selectEvalContext = createSelector(
  [
    (state) => selectData(state).trips,
    (state) => selectData(state).locations,
    (state) => selectData(state).vehicles,
    (state) => selectData(state).vehicle_types,
    (state) => selectData(state).travel_costs,
    selectTimeUnit,
  ],
  (trips, locations, vehicles, vehicle_types, travel_costs, timeUnit) =>
    buildEvalContext({ trips, locations, vehicles, vehicle_types, travel_costs }, timeUnit)
);

const hasBaselineContent = (baseline) =>
  Boolean(baseline && Array.isArray(baseline.route_stops) && baseline.route_stops.length > 0);

export const selectResultBaseline = (state) => {
  const baseline = selectData(state).resultBaseline;
  return hasBaselineContent(baseline) ? baseline : null;
};

// Stable fallback: before any manual edit exists (the normal case) a fresh `[]`
// per call re-rendered the whole VehicleRoutesDashboard subtree on every store
// dispatch — including the solve-status poll every few seconds.
const NO_AMENDMENTS = Object.freeze([]);

export const selectResultAmendments = (state) => {
  const amendments = selectData(state).resultAmendments;
  return Array.isArray(amendments) ? amendments : NO_AMENDMENTS;
};

export const selectHasAmendments = (state) => selectResultAmendments(state).length > 0;

/**
 * Golden fidelity report for the pristine solver baseline. `null` when no
 * baseline exists. Memoized: computed at most once per (baseline, tables)
 * pair, the first time the results panel asks for it.
 */
export const selectBaselineFidelity = createSelector(
  [selectResultBaseline, selectEvalContext],
  (baseline, ctx) => {
    if (!baseline) return null;
    return goldenCheck(baseline, ctx);
  }
);

/**
 * Live evaluation of the pending (uncommitted) amendment for the review
 * drawer. Recomputed only when the pending edit or the data changes; the
 * commit reducer re-runs the same pure evaluation, so this preview can never
 * diverge from what a commit would do.
 */
export const selectPendingEvaluation = createSelector(
  [selectData, (state) => state.ui?.resultEditing?.pendingAmendment ?? null, selectEvalContext],
  (data, pendingAmendment, ctx) => {
    if (!pendingAmendment) return null;
    return evaluateAmendment(data, pendingAmendment, ctx);
  }
);

/** Single-level repair suggestions for a violating pending edit. */
export const selectRepairSuggestions = createSelector(
  [selectData, (state) => state.ui?.resultEditing?.pendingAmendment ?? null, selectPendingEvaluation, selectEvalContext],
  (data, pendingAmendment, pendingEvaluation, ctx) => {
    if (!pendingAmendment || !pendingEvaluation) return [];
    return suggestRepairs(data, pendingAmendment, pendingEvaluation, ctx);
  }
);

/** Stop keys implicated in the pending edit's violations, for row highlighting. */
export const selectViolatedStopKeys = createSelector([selectPendingEvaluation], (evaluation) => {
  const keys = new Set();
  if (evaluation && !evaluation.error) {
    for (const v of evaluation.violations) {
      if (v.entities && v.entities.stopKey) keys.add(v.entities.stopKey);
    }
  }
  return keys;
});
